import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { buscarClientePorCnpj } from '../../../services/clientes';
import { buscarDadosPaleteCarregamento, criarConferencia, registrarLeitura, listarPaletesCarregamento } from '../../../services/conferencia';
import type { PaleteCarregamento } from '../../../services/conferencia';
import { buscarClienteOfflinePorCnpj, buscarDadosPaleteCarregamentoOffline, listarPaletesCarregamentoOffline } from '../../../services/offlineData';
import { salvarLeituraPendente, salvarConferenciaPendente, gerarTempConferenciaId } from '../../../services/offlineQueue';
import { buscarConfiguracoesConferencia } from '../../../services/configuracoesConferencia';
import type { ConfiguracaoConferencia } from '../../../services/configuracoesConferencia';
import { buscarClientesConferente } from '../../../services/conferente';
import type { ClienteConferente } from '../../../services/conferente';

import '../PreCarregamento/PreCarregamento.css';

type TipoLocal = 'cliente' | 'base';

type Etapa =
  | 'tipo'
  | 'cnpj'
  | 'cnpjs_base'
  | 'veiculo_doca'
  | 'palete'
  | 'lista_paletes'
  | 'dados_doca'
  | 'veiculo'
  | 'confirmar'
  | 'sucesso';

interface ClienteInfo {
  id: number;
  razao_social: string;
  cnpj: string;
}

interface PaleteDados {
  id: number;
  numero_palete: number;
  doca: string | null;
  area_nome: string;
  area_codigo: string;
  area_regiao: string;
  operacao_id: number;
  numero_operacao: number;
  tipo_palete?: string;
  data_agendamento?: string;
}

interface LeituraPreCarregamento {
  codigo_lido: string;
  dados_decodificados: Record<string, unknown>;
  lido_em: string;
  tipo_leitura: string;
}

interface PaleteCarregadoInfo {
  numero_palete: number;
  area_nome: string;
  area_codigo: string;
  area_regiao: string;
  numero_operacao: number;
  tipo_palete?: string;
  total_volumes: number;
  peso_total: number;
  cubagem_total: number;
  volumes_logisticos: number;
  pedidos: string[];
  cliente_razao_social: string;
  carregado_em: string;
}

interface CarregamentoProps {
  backUrl?: string;
}

const CarregamentoConferencia: React.FC<CarregamentoProps> = ({ backUrl = '/conferencia' }) => {
  const navigate = useNavigate();
  const isPWA = backUrl === '/conferente';

  const [etapa, setEtapa] = useState<Etapa>('tipo');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inicializando, setInicializando] = useState(true);

  // Configurações globais de conferência
  const [configGlobal, setConfigGlobal] = useState<ConfiguracaoConferencia | null>(null);

  // Config
  const [tipoLocal, setTipoLocal] = useState<TipoLocal>('cliente');

  // Cliente (modo "No Cliente")
  const [cnpj, setCnpj] = useState('');
  const [cliente, setCliente] = useState<ClienteInfo | null>(null);

  // Clientes (modo "Na Base")
  const [cnpjBase, setCnpjBase] = useState('');
  const [clientesBase, setClientesBase] = useState<ClienteInfo[]>([]);
  const [loadingCnpjBase, setLoadingCnpjBase] = useState(false);

  // Palete
  const [numeroPalete, setNumeroPalete] = useState('');
  const [paleteDados, setPaleteDados] = useState<PaleteDados | null>(null);
  const [leituras, setLeituras] = useState<LeituraPreCarregamento[]>([]);
  const [totalVolumes, setTotalVolumes] = useState(0);
  const [totaisLogPalete, setTotaisLogPalete] = useState({ peso_total: 0, cubagem_total: 0, volumes_total: 0 });
  const [pedidosPalete, setPedidosPalete] = useState<string[]>([]);
  const [clientePaleteAtual, setClientePaleteAtual] = useState<ClienteInfo | null>(null);
  const paleteRef = useRef<HTMLInputElement>(null);

  // Doca e Veiculo
  const [doca, setDoca] = useState('');
  const [placaVeiculo, setPlacaVeiculo] = useState('');
  const docaRef = useRef<HTMLInputElement>(null);
  const veiculoRef = useRef<HTMLInputElement>(null);
  const [showKeyboard, setShowKeyboard] = useState(false);

  // Conferencia
  const [conferenciaId, setConferenciaId] = useState<number | null>(null);
  const [totalConferidos, setTotalConferidos] = useState(0);
  const [paletesConferidos, setPaletesConferidos] = useState<number[]>([]);

  // Dados dos paletes já carregados (modo individual)
  const [paletesCarregadosDados, setPaletesCarregadosDados] = useState<PaleteCarregadoInfo[]>([]);

  // Lista de Paletes (modo lista)
  const [paletesLista, setPaletesLista] = useState<PaleteCarregamento[]>([]);
  const [paletesSelecionados, setPaletesSelecionados] = useState<Set<number>>(new Set());
  const [loadingLista, setLoadingLista] = useState(false);

  // Clientes vinculados ao conferente (filtro de paletes no modo lista)
  const [clientesConferente, setClientesConferente] = useState<ClienteConferente[]>([]);
  const [clienteConferenteSelecionado, setClienteConferenteSelecionado] = useState<number | null>(null);
  const [semVinculosConferente, setSemVinculosConferente] = useState(false);
  const [loadingClientesConferente, setLoadingClientesConferente] = useState(false);

  // Visualização de detalhes do palete carregado
  const [paleteDetalheIdx, setPaleteDetalheIdx] = useState<number | null>(null);

  // Carregar configurações globais e inicializar
  useEffect(() => {
    const carregarConfigs = async () => {
      try {
        const result = await buscarConfiguracoesConferencia();
        if (result.success && result.configuracao) {
          setConfigGlobal(result.configuracao);
        }
      } catch {
        // Fallback: usar defaults se não conseguir carregar
      }
      if (isPWA) {
        setTipoLocal('cliente');
        setEtapa('veiculo_doca');
      }
      setInicializando(false);
    };
    carregarConfigs();
  }, [isPWA]);

  useEffect(() => {
    if (etapa === 'palete' && paleteRef.current) paleteRef.current.focus();
    if (etapa === 'dados_doca' && docaRef.current) docaRef.current.focus();
    if (etapa === 'veiculo' && veiculoRef.current) veiculoRef.current.focus();
    if (etapa === 'veiculo_doca' && veiculoRef.current) veiculoRef.current.focus();
  }, [etapa]);

  const formatCnpj = (value: string): string => {
    const nums = value.replace(/\D/g, '').slice(0, 14);
    if (nums.length <= 2) return nums;
    if (nums.length <= 5) return `${nums.slice(0, 2)}.${nums.slice(2)}`;
    if (nums.length <= 8) return `${nums.slice(0, 2)}.${nums.slice(2, 5)}.${nums.slice(5)}`;
    if (nums.length <= 12) return `${nums.slice(0, 2)}.${nums.slice(2, 5)}.${nums.slice(5, 8)}/${nums.slice(8)}`;
    return `${nums.slice(0, 2)}.${nums.slice(2, 5)}.${nums.slice(5, 8)}/${nums.slice(8, 12)}-${nums.slice(12)}`;
  };

  const formatPlaca = (value: string): string => {
    return value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7);
  };

  // =========================================
  // Step: Tipo (only for non-PWA)
  // =========================================
  const handleSelecionarTipo = (tipo: TipoLocal) => {
    setTipoLocal(tipo);
    setError('');
    if (tipo === 'cliente') {
      setEtapa('cnpj');
    } else {
      setEtapa('cnpjs_base');
    }
  };

  // =========================================
  // Step: CNPJ (modo "No Cliente")
  // =========================================
  const handleConsultarCnpj = async (e: React.FormEvent) => {
    e.preventDefault();
    const cnpjLimpo = cnpj.replace(/\D/g, '');
    if (cnpjLimpo.length !== 14) {
      setError('CNPJ deve conter 14 dígitos');
      return;
    }
    setError('');
    setLoading(true);
    try {
      let clienteData: any = null;
      try {
        const result = await buscarClientePorCnpj(cnpjLimpo);
        if (result.success && result.cliente) {
          clienteData = result.cliente;
        }
      } catch (apiErr: any) {
        if (!navigator.onLine) {
          clienteData = await buscarClienteOfflinePorCnpj(cnpjLimpo);
          if (!clienteData) throw new Error('Cliente não encontrado no cache offline');
        } else {
          throw apiErr;
        }
      }
      if (clienteData) {
        setCliente({ id: clienteData.id, razao_social: clienteData.razao_social, cnpj: clienteData.cnpj });
        // Usar configuração global para definir o fluxo
        setEtapa('veiculo_doca');
      }
    } catch (err: any) {
      setError(err.message || 'Cliente não encontrado para este CNPJ');
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // Step: CNPJs Base (modo "Na Base")
  // =========================================
  const handleAdicionarCnpjBase = async (e: React.FormEvent) => {
    e.preventDefault();
    const cnpjLimpo = cnpjBase.replace(/\D/g, '');
    if (cnpjLimpo.length !== 14) {
      setError('CNPJ deve conter 14 dígitos');
      return;
    }
    if (clientesBase.some(c => c.cnpj.replace(/\D/g, '') === cnpjLimpo)) {
      setError('Este CNPJ já foi adicionado à lista');
      return;
    }
    setError('');
    setLoadingCnpjBase(true);
    try {
      let clienteData: any = null;
      try {
        const result = await buscarClientePorCnpj(cnpjLimpo);
        if (result.success && result.cliente) {
          clienteData = result.cliente;
        }
      } catch (apiErr: any) {
        if (!navigator.onLine) {
          clienteData = await buscarClienteOfflinePorCnpj(cnpjLimpo);
          if (!clienteData) throw new Error('Cliente não encontrado no cache offline');
        } else {
          throw apiErr;
        }
      }
      if (clienteData) {
        setClientesBase(prev => [...prev, {
          id: clienteData.id,
          razao_social: clienteData.razao_social,
          cnpj: clienteData.cnpj
        }]);
        setCnpjBase('');
      }
    } catch (err: any) {
      setError(err.message || 'Cliente não encontrado para este CNPJ');
    } finally {
      setLoadingCnpjBase(false);
    }
  };

  const handleRemoverClienteBase = (clienteId: number) => {
    setClientesBase(prev => prev.filter(c => c.id !== clienteId));
  };

  const handleIniciarBase = () => {
    if (clientesBase.length === 0) {
      setError('Adicione ao menos um destinatário');
      return;
    }
    setError('');
    setEtapa('veiculo_doca');
  };

  // =========================================
  // Step: Veiculo + Doca (primeiro passo)
  // =========================================
  // A doca agora é exibida junto com o veículo, usando configuração global
  const deveExibirDoca = (): boolean => {
    return configGlobal ? configGlobal.exibir_doca !== false : true;
  };

  // Modo de carregamento global
  const getModoCarregamento = (): 'palete_individual' | 'lista_paletes' => {
    return configGlobal?.modo_carregamento || 'palete_individual';
  };

  const handleConfirmarVeiculoDoca = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!placaVeiculo.trim() || placaVeiculo.length < 7) {
      setError('Informe a placa do veículo (7 caracteres)');
      return;
    }
    // Validar doca se exibir_doca está ativo
    if (deveExibirDoca() && !doca.trim()) {
      setError('Informe o número da doca');
      return;
    }
    setError('');
    // Se modo lista de paletes (config global), ir para lista
    if (getModoCarregamento() === 'lista_paletes') {
      setEtapa('lista_paletes');
    } else {
      setEtapa('palete');
    }
  };

  // =========================================
  // Step: Palete
  // =========================================
  const handleConsultarPalete = async (e: React.FormEvent) => {
    e.preventDefault();
    const numPalete = parseInt(numeroPalete);
    if (!numPalete || numPalete <= 0) {
      setError('Informe um número de palete válido');
      return;
    }
    if (paletesConferidos.includes(numPalete)) {
      setError(`Palete #${numPalete} já foi conferido nesta sessão`);
      return;
    }
    setError('');
    setLoading(true);

    try {
      let encontrado = false;
      // Variável local para rastrear o cliente detectado (setCliente/setClientePaleteAtual são assíncronos)
      let clienteLocal: ClienteInfo | null = null;

      if (isPWA && !cliente) {
        // PWA sem cliente selecionado: buscar sem filtro de cliente
        try {
          const result = await buscarDadosPaleteCarregamento(null, numPalete);
          if (result.success) {
            setPaleteDados(result.palete);
            setLeituras(result.leituras);
            setTotalVolumes(result.total_volumes);
            if (result.totais_logisticos) {
              setTotaisLogPalete({
                peso_total: result.totais_logisticos.peso_total || 0,
                cubagem_total: result.totais_logisticos.cubagem_total || 0,
                volumes_total: result.totais_logisticos.volumes_total || 0,
              });
            }
            setPedidosPalete(result.pedidos || []);
            // Derivar cliente da resposta do palete
            if (result.cliente) {
              clienteLocal = {
                id: result.cliente.id,
                razao_social: result.cliente.razao_social,
                cnpj: result.cliente.cnpj,
              };
              setCliente(clienteLocal);
              setClientePaleteAtual(clienteLocal);
            } else {
              // Criar cliente placeholder se não retornou
              clienteLocal = { id: 0, razao_social: 'Cliente não identificado', cnpj: '' };
              setClientePaleteAtual(clienteLocal);
            }
            encontrado = true;
          }
        } catch (apiErr: any) {
          const errMsg = apiErr?.message || '';
          // Verificar se palete já foi carregado (409)
          if (errMsg.includes('já foi carregado')) {
            setError(`Palete #${numPalete} já foi carregado`);
            setNumeroPalete('');
            return;
          }
          // Verificar se palete foi inutilizado
          if (errMsg.includes('inutilizado') || errMsg.includes('Palete Nao Utilizado')) {
            setError(`Palete #${numPalete} foi inutilizado e nao pode ser utilizado. Este palete recebeu uma ocorrencia automatica.`);
            setNumeroPalete('');
            return;
          }
          // Palete não encontrado (404) - não propagar erro, será tratado abaixo
          if (!errMsg.includes('não encontrad')) {
            if (!navigator.onLine) {
              try {
                const offlineResult = await buscarDadosPaleteCarregamentoOffline(null, numPalete);
                if (offlineResult && offlineResult.success) {
                  setPaleteDados(offlineResult.palete);
                  setLeituras(offlineResult.leituras || []);
                  setTotalVolumes(offlineResult.total_volumes || 0);
                  if (offlineResult.cliente) {
                    clienteLocal = {
                      id: offlineResult.cliente.id,
                      razao_social: offlineResult.cliente.razao_social,
                      cnpj: offlineResult.cliente.cnpj,
                    };
                    setCliente(clienteLocal);
                    setClientePaleteAtual(clienteLocal);
                  }
                  encontrado = true;
                }
              } catch { /* continuar */ }
            }
          }
        }
      } else {
        // Fluxo legacy ou PWA com cliente já definido: buscar por cliente(s)
        const clientesParaBusca = tipoLocal === 'cliente'
          ? (cliente ? [cliente] : [])
          : clientesBase;

        for (const cl of clientesParaBusca) {
          try {
            const result = await buscarDadosPaleteCarregamento(cl.id, numPalete);
            if (result.success) {
              setPaleteDados(result.palete);
              setLeituras(result.leituras);
              setTotalVolumes(result.total_volumes);
              if (result.totais_logisticos) {
                setTotaisLogPalete({
                  peso_total: result.totais_logisticos.peso_total || 0,
                  cubagem_total: result.totais_logisticos.cubagem_total || 0,
                  volumes_total: result.totais_logisticos.volumes_total || 0,
                });
              }
              setPedidosPalete(result.pedidos || []);
              clienteLocal = cl;
              setClientePaleteAtual(cl);
              encontrado = true;
              break;
            }
          } catch (apiErr: any) {
            const errMsg = apiErr?.message || '';
            // Verificar se palete já foi carregado (409)
            if (errMsg.includes('já foi carregado')) {
              setError(`Palete #${numPalete} já foi carregado`);
              setNumeroPalete('');
              return;
            }
            // Palete não encontrado (404) - continuar buscando nos próximos clientes
            if (errMsg.includes('não encontrad')) {
              continue;
            }
            if (!navigator.onLine) {
              try {
                const offlineResult = await buscarDadosPaleteCarregamentoOffline(cl.id, numPalete);
                if (offlineResult && offlineResult.success) {
                  setPaleteDados(offlineResult.palete);
                  setLeituras(offlineResult.leituras || []);
                  setTotalVolumes(offlineResult.total_volumes || 0);
                  clienteLocal = cl;
                  setClientePaleteAtual(cl);
                  encontrado = true;
                  break;
                }
              } catch { /* continuar */ }
            }
          }
        }
      }

      if (!encontrado) {
        setError(`Palete #${numPalete} não encontrado em nenhuma operação ativa${tipoLocal === 'base' ? ' dos destinatários informados' : ''}`);
        setNumeroPalete('');
        return;
      }

      // Doca já foi informada junto com o veículo, registrar direto
      if (placaVeiculo) {
        await handleRegistrarConferencia();
      } else {
        // Legacy flow sem veículo: solicitar veículo
        setEtapa('veiculo');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao consultar palete');
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // Step: Dados + Doca (legacy flow)
  // =========================================
  const handleConfirmarDoca = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!doca.trim()) {
      setError('Informe o número da doca');
      return;
    }
    setError('');

    if (!placaVeiculo) {
      setEtapa('veiculo');
    } else {
      // Veículo e doca já definidos, registrar direto sem tela de confirmação
      await handleRegistrarConferencia();
    }
  };

  // =========================================
  // Step: Veiculo (legacy flow)
  // =========================================
  const handleConfirmarVeiculo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!placaVeiculo.trim()) {
      setError('Informe a placa do veículo');
      return;
    }
    setError('');
    await handleRegistrarConferencia();
  };

  // =========================================
  // Step: Confirmar (pallets subsequentes)
  // =========================================
  const handleConfirmar = async () => {
    setError('');
    await handleRegistrarConferencia();
  };

  // =========================================
  // Registrar conferencia
  // =========================================
  const handleRegistrarConferencia = async () => {
    if (!paleteDados || !clientePaleteAtual) return;
    setLoading(true);

    try {
      // Create conference if not yet created
      let confId = conferenciaId;
      if (!confId) {
        try {
          const clienteIdConf = tipoLocal === 'cliente' && clientePaleteAtual.id ? clientePaleteAtual.id : undefined;
          const confResult = await criarConferencia({
            tipo: 'carregamento',
            cliente_id: clienteIdConf,
            placa_veiculo: placaVeiculo,
            observacoes: tipoLocal === 'base'
              ? `Carregamento na base - ${clientesBase.length} destinatário(s)`
              : `Carregamento - ${clientePaleteAtual.razao_social}`
          });
          if (confResult.success) {
            confId = confResult.conferencia.id;
          } else {
            throw new Error('Falha ao criar conferência no servidor');
          }
        } catch (confErr: any) {
          if (!navigator.onLine) {
            confId = await gerarTempConferenciaId();
            await salvarConferenciaPendente({
              temp_id: confId,
              tipo: 'carregamento',
              cliente_id: tipoLocal === 'cliente' && clientePaleteAtual.id ? clientePaleteAtual.id : undefined,
              placa_veiculo: placaVeiculo,
              observacoes: tipoLocal === 'base'
                ? `Carregamento na base - ${clientesBase.length} destinatário(s)`
                : `Carregamento no cliente - ${clientePaleteAtual.razao_social}`,
            });
          } else {
            throw confErr;
          }
        }
        setConferenciaId(confId);
      }

      // Register reading
      if (confId) {
        const dadosDecodificados: Record<string, unknown> = {
          numero_palete: paleteDados.numero_palete,
          area_entrega: paleteDados.area_nome,
          area_regiao: paleteDados.area_regiao,
          operacao: paleteDados.numero_operacao,
          doca: doca,
          placa_veiculo: placaVeiculo,
          total_volumes_pre_carregamento: totalVolumes,
          cliente_razao_social: clientePaleteAtual.razao_social
        };

        try {
          await registrarLeitura(confId, {
            tipo_leitura: 'manual',
            codigo_lido: `PALETE-${paleteDados.numero_palete}-OP${paleteDados.numero_operacao}`,
            dados_decodificados: dadosDecodificados,
            quantidade: totalVolumes || 1
          });
        } catch (leitErr: any) {
          if (!navigator.onLine) {
            await salvarLeituraPendente({
              conferencia_id: confId,
              tipo_leitura: 'manual',
              codigo_lido: `PALETE-${paleteDados.numero_palete}-OP${paleteDados.numero_operacao}`,
              quantidade: totalVolumes || 1,
              lido_em: new Date().toISOString(),
              dados_decodificados: dadosDecodificados,
            });
          } else {
            throw leitErr;
          }
        }
      } else {
        throw new Error('Não foi possível criar a conferência');
      }

      // Salvar dados do palete carregado
      const paleteInfo: PaleteCarregadoInfo = {
        numero_palete: paleteDados.numero_palete,
        area_nome: paleteDados.area_nome,
        area_codigo: paleteDados.area_codigo,
        area_regiao: paleteDados.area_regiao,
        numero_operacao: paleteDados.numero_operacao,
        tipo_palete: paleteDados.tipo_palete,
        total_volumes: totalVolumes,
        peso_total: totaisLogPalete.peso_total,
        cubagem_total: totaisLogPalete.cubagem_total,
        volumes_logisticos: totaisLogPalete.volumes_total,
        pedidos: pedidosPalete,
        cliente_razao_social: clientePaleteAtual.razao_social,
        carregado_em: new Date().toISOString(),
      };

      setTotalConferidos(prev => prev + 1);
      setPaletesConferidos(prev => [...prev, paleteDados.numero_palete]);
      setPaletesCarregadosDados(prev => [paleteInfo, ...prev]);

      // Voltar direto para o input do próximo palete
      setNumeroPalete('');
      setPaleteDados(null);
      setLeituras([]);
      setTotalVolumes(0);
      setTotaisLogPalete({ peso_total: 0, cubagem_total: 0, volumes_total: 0 });
      setPedidosPalete([]);
      setClientePaleteAtual(null);
      setError('');
      setEtapa('palete');
    } catch (err: any) {
      setError(err.message || 'Erro ao registrar conferência');
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // Lista de Paletes (modo lista)
  // =========================================
  const carregarListaPaletes = async (clienteId: number | null) => {
    setLoadingLista(true);
    setError('');
    try {
      const result = await listarPaletesCarregamento(clienteId);
      if (result.success) {
        setPaletesLista(result.paletes);
      }
    } catch (err: any) {
      // Fallback offline
      const offlineResult = await listarPaletesCarregamentoOffline(clienteId);
      if (offlineResult && offlineResult.success) {
        setPaletesLista(offlineResult.paletes);
      } else {
        setError(err.message || 'Erro ao carregar lista de paletes');
      }
    } finally {
      setLoadingLista(false);
    }
  };

  // Carregar clientes do conferente e lista de paletes quando entra na etapa lista_paletes
  useEffect(() => {
    if (etapa === 'lista_paletes') {
      // Se já tem um cliente selecionado (modo "No Cliente"), usar direto
      if (tipoLocal === 'cliente' && cliente) {
        carregarListaPaletes(cliente.id);
        return;
      }

      // Modo "Na Base" (não-PWA): carregar sem filtro de conferente (admin já selecionou os CNPJs)
      if (tipoLocal === 'base' && !isPWA) {
        carregarListaPaletes(null);
        return;
      }

      // PWA: buscar clientes vinculados ao conferente para filtrar paletes
      const carregarClientesEPaletes = async () => {
        setLoadingClientesConferente(true);
        try {
          const result = await buscarClientesConferente();
          if (result.success) {
            if (result.sem_vinculos || result.clientes.length === 0) {
              // Conferente sem vínculos: não listar paletes
              setSemVinculosConferente(true);
              setClientesConferente([]);
              setPaletesLista([]);
              return;
            }

            setSemVinculosConferente(false);
            setClientesConferente(result.clientes);

            if (result.auto_select && result.clientes.length === 1) {
              // Apenas 1 cliente vinculado: auto-selecionar e carregar paletes
              const clienteUnico = result.clientes[0];
              setClienteConferenteSelecionado(clienteUnico.id);
              carregarListaPaletes(clienteUnico.id);
            } else {
              // Múltiplos clientes: aguardar seleção do conferente
              setPaletesLista([]);
            }
          }
        } catch {
          // Fallback: carregar sem filtro de cliente (comportamento anterior)
          carregarListaPaletes(null);
        } finally {
          setLoadingClientesConferente(false);
        }
      };

      carregarClientesEPaletes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etapa]);

  const handleTogglePalete = (paleteId: number, disponivel: boolean) => {
    if (!disponivel) return; // Não permitir selecionar paletes com divergência
    setPaletesSelecionados(prev => {
      const next = new Set(prev);
      if (next.has(paleteId)) {
        next.delete(paleteId);
      } else {
        next.add(paleteId);
      }
      return next;
    });
  };

  const handleSelecionarTodos = () => {
    const disponiveis = paletesLista.filter(p => p.disponivel);
    if (disponiveis.length === 0) return; // Nenhum palete disponível
    if (paletesSelecionados.size === disponiveis.length) {
      // Desselecionar todos
      setPaletesSelecionados(new Set());
    } else {
      // Selecionar todos os disponíveis (nunca seleciona divergentes)
      setPaletesSelecionados(new Set(disponiveis.map(p => p.id)));
    }
  };

  const handleConfirmarListaPaletes = async () => {
    if (paletesSelecionados.size === 0) {
      setError('Selecione ao menos um palete');
      return;
    }
    setError('');
    setLoading(true);

    const clienteAtual = tipoLocal === 'cliente' ? cliente : null;

    try {
      // Criar conferência se não existe
      let confId = conferenciaId;
      if (!confId) {
        try {
          const confResult = await criarConferencia({
            tipo: 'carregamento',
            cliente_id: clienteAtual?.id,
            placa_veiculo: placaVeiculo,
            observacoes: `Carregamento - ${clienteAtual?.razao_social || 'Múltiplos clientes'} (${paletesSelecionados.size} paletes)`
          });
          if (confResult.success) {
            confId = confResult.conferencia.id;
          } else {
            throw new Error('Falha ao criar conferência no servidor');
          }
        } catch (confErr: any) {
          if (!navigator.onLine) {
            confId = await gerarTempConferenciaId();
            await salvarConferenciaPendente({
              temp_id: confId,
              tipo: 'carregamento',
              cliente_id: clienteAtual?.id,
              placa_veiculo: placaVeiculo,
              observacoes: `Carregamento - ${clienteAtual?.razao_social || 'Múltiplos clientes'} (${paletesSelecionados.size} paletes)`,
            });
          } else {
            throw confErr;
          }
        }
        setConferenciaId(confId);
      }

      // Registrar leitura para cada palete selecionado
      if (confId) {
        const paletesSel = paletesLista.filter(p => paletesSelecionados.has(p.id));
        const paletesRegistrados: number[] = [];
        const paletesFalharam: number[] = [];

        for (const pal of paletesSel) {
          const dadosDecodificados: Record<string, unknown> = {
            numero_palete: pal.numero_palete,
            area_entrega: pal.area_nome,
            area_regiao: pal.area_regiao,
            operacao: pal.numero_operacao,
            doca: doca || null,
            placa_veiculo: placaVeiculo,
            total_volumes_pre_carregamento: pal.total_volumes,
            cliente_razao_social: clienteAtual?.razao_social || ''
          };

          try {
            await registrarLeitura(confId, {
              tipo_leitura: 'manual',
              codigo_lido: `PALETE-${pal.numero_palete}-OP${pal.numero_operacao}`,
              dados_decodificados: dadosDecodificados,
              quantidade: pal.total_volumes || 1
            });
            paletesRegistrados.push(pal.numero_palete);
          } catch (leitErr: any) {
            if (!navigator.onLine) {
              await salvarLeituraPendente({
                conferencia_id: confId,
                tipo_leitura: 'manual',
                codigo_lido: `PALETE-${pal.numero_palete}-OP${pal.numero_operacao}`,
                quantidade: pal.total_volumes || 1,
                lido_em: new Date().toISOString(),
                dados_decodificados: dadosDecodificados,
              });
              paletesRegistrados.push(pal.numero_palete);
            } else {
              paletesFalharam.push(pal.numero_palete);
            }
          }
        }

        if (paletesRegistrados.length > 0) {
          setTotalConferidos(paletesRegistrados.length);
          setPaletesConferidos(paletesRegistrados);

          // Popular dados detalhados dos paletes para exibição na tela de sucesso
          const dadosDetalhados: PaleteCarregadoInfo[] = paletesSel
            .filter(p => paletesRegistrados.includes(p.numero_palete))
            .map(p => ({
              numero_palete: p.numero_palete,
              area_nome: p.area_nome,
              area_codigo: p.area_codigo,
              area_regiao: p.area_regiao,
              numero_operacao: p.numero_operacao,
              tipo_palete: p.tipo_palete,
              total_volumes: p.total_volumes,
              peso_total: p.peso_total || 0,
              cubagem_total: p.cubagem_total || 0,
              volumes_logisticos: p.volumes_logisticos || 0,
              pedidos: p.pedidos || [],
              cliente_razao_social: clienteAtual?.razao_social || '',
              carregado_em: new Date().toISOString(),
            }));
          setPaletesCarregadosDados(dadosDetalhados);

          setEtapa('sucesso');
          if (paletesFalharam.length > 0) {
            setError(`Atenção: ${paletesFalharam.length} palete(s) não puderam ser registrados (${paletesFalharam.map(n => `#${n}`).join(', ')}). Os demais foram registrados com sucesso.`);
          }
        } else {
          throw new Error('Nenhum palete pôde ser registrado. Verifique sua conexão e tente novamente.');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao registrar conferência');
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // Navegar
  // =========================================
  const handleFinalizarCarregamento = () => {
    if (paletesConferidos.length === 0) return;
    setError('');
    setEtapa('sucesso');
  };

  const handleVoltar = () => {
    setError('');
    if (etapa === 'cnpj' || etapa === 'cnpjs_base') {
      setEtapa('tipo');
    } else if (etapa === 'veiculo_doca') {
      if (isPWA) {
        navigate(backUrl);
        return;
      }
      // Voltar para seleção de cliente
      if (tipoLocal === 'cliente') {
        setEtapa('cnpj');
      } else if (tipoLocal === 'base') {
        setEtapa('cnpjs_base');
      } else {
        setEtapa('tipo');
      }
    } else if (etapa === 'lista_paletes') {
      setPaletesSelecionados(new Set());
      setPaletesLista([]);
      setClientesConferente([]);
      setClienteConferenteSelecionado(null);
      setSemVinculosConferente(false);
      setEtapa('veiculo_doca');
    } else if (etapa === 'palete') {
      setEtapa('veiculo_doca');
    } else if (etapa === 'dados_doca') {
      setNumeroPalete('');
      setEtapa('palete');
    } else if (etapa === 'veiculo') {
      setNumeroPalete('');
      setDoca('');
      setEtapa('palete');
    } else if (etapa === 'confirmar') {
      setNumeroPalete('');
      setEtapa('palete');
    }
  };

  const handleReset = () => {
    setCnpj('');
    setCliente(null);
    setCnpjBase('');
    setClientesBase([]);
    setNumeroPalete('');
    setPaleteDados(null);
    setLeituras([]);
    setTotalVolumes(0);
    setTotaisLogPalete({ peso_total: 0, cubagem_total: 0, volumes_total: 0 });
    setPedidosPalete([]);
    setClientePaleteAtual(null);
    setDoca('');
    setPlacaVeiculo('');
    setConferenciaId(null);
    setTotalConferidos(0);
    setPaletesConferidos([]);
    setPaletesCarregadosDados([]);
    setPaletesLista([]);
    setPaletesSelecionados(new Set());
    setPaleteDetalheIdx(null);
    setError('');
    if (isPWA) {
      navigate(backUrl);
      return;
    }
    setEtapa('tipo');
  };

  // =========================================
  // Step indicator
  // =========================================
  const renderStepIndicator = () => {
    const isListaMode = getModoCarregamento() === 'lista_paletes';
    if (isPWA) {
      const steps = [
        { key: 'veiculo_doca', label: 'Veículo', num: 1 },
        { key: 'palete', label: isListaMode ? 'Seleção' : 'Palete', num: 2 },
        { key: 'sucesso', label: 'Resultado', num: 3 }
      ];

      const getIdx = () => {
        if (etapa === 'veiculo_doca') return 0;
        if (etapa === 'palete' || etapa === 'lista_paletes' || etapa === 'dados_doca' || etapa === 'confirmar') return 1;
        return 2;
      };
      const currentIdx = getIdx();

      return (
        <div className="precarreg-steps">
          {steps.map((step, idx) => (
            <div key={step.key} className={`precarreg-step ${idx <= currentIdx ? 'active' : ''} ${idx < currentIdx ? 'completed' : ''}`}>
              <div className="step-circle">
                {idx < currentIdx ? <i className="fas fa-check"></i> : step.num}
              </div>
              <span className="step-label">{step.label}</span>
              {idx < steps.length - 1 && <div className="step-line" />}
            </div>
          ))}
        </div>
      );
    }

    // Legacy flow: Local -> Cliente -> Veículo -> Palete -> Resultado
    const steps = [
      { key: 'tipo', label: 'Local', num: 1 },
      { key: 'cnpj', label: 'Cliente', num: 2 },
      { key: 'veiculo_doca', label: 'Veículo', num: 3 },
      { key: 'palete', label: isListaMode ? 'Seleção' : 'Palete', num: 4 },
      { key: 'sucesso', label: 'Resultado', num: 5 }
    ];

    const getIdx = () => {
      if (etapa === 'tipo') return 0;
      if (etapa === 'cnpj' || etapa === 'cnpjs_base') return 1;
      if (etapa === 'veiculo_doca') return 2;
      if (etapa === 'palete' || etapa === 'lista_paletes' || etapa === 'dados_doca' || etapa === 'veiculo' || etapa === 'confirmar') return 3;
      return 4;
    };
    const currentIdx = getIdx();

    return (
      <div className="precarreg-steps">
        {steps.map((step, idx) => (
          <div key={step.key} className={`precarreg-step ${idx <= currentIdx ? 'active' : ''} ${idx < currentIdx ? 'completed' : ''}`}>
            <div className="step-circle">
              {idx < currentIdx ? <i className="fas fa-check"></i> : step.num}
            </div>
            <span className="step-label">{step.label}</span>
            {idx < steps.length - 1 && <div className="step-line" />}
          </div>
        ))}
      </div>
    );
  };

  // =========================================
  // Client info bar helper
  // =========================================
  const renderClienteInfo = (cl: ClienteInfo) => (
    <div className="precarreg-cliente-info">
      <span className="cliente-nome">{cl.razao_social}</span>
      <span className="cliente-cnpj">CNPJ: {formatCnpj(cl.cnpj)}</span>
    </div>
  );

  if (inicializando) {
    return (
      <div className={isPWA ? 'pwa-page-wrapper' : ''}>
        {isPWA && (
          <div className="pwa-green-header">
            <button className="pwa-header-back" onClick={() => navigate(backUrl)} aria-label="Voltar ao dashboard">
              <i className="fas fa-arrow-left"></i>
            </button>
            <div className="pwa-header-info">
              <span className="pwa-header-title">Carregamento</span>
            </div>
          </div>
        )}
        <div className="page-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <div style={{ textAlign: 'center' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#3498db' }}></i>
            <p style={{ marginTop: 12, color: '#7f8c8d' }}>Carregando...</p>
          </div>
        </div>
      </div>
    );
  }

  // =========================================
  // Render
  // =========================================
  return (
    <div className={isPWA ? 'pwa-page-wrapper' : ''}>
      {isPWA && (
        <div className="pwa-green-header">
          <button className="pwa-header-back" onClick={() => navigate(backUrl)} aria-label="Voltar ao dashboard">
            <i className="fas fa-arrow-left"></i>
          </button>
          <div className="pwa-header-info">
            <span className="pwa-header-title">Carregamento</span>
            {totalConferidos > 0 && (
              <span className="pwa-header-badge">{totalConferidos} palete{totalConferidos !== 1 ? 's' : ''}</span>
            )}
          </div>
        </div>
      )}
      <div className={isPWA ? 'page-container pwa-content' : 'page-container'}>
      {!isPWA && (
        <button className="conferencia-back-link" onClick={() => navigate(backUrl)}>
          <i className="fas fa-arrow-left"></i> Voltar
        </button>
      )}

      <div className="precarreg-container">
        {!isPWA && (
        <div className="precarreg-header">
          <div className="precarreg-title">
            <i className="fas fa-truck-loading"></i>
            <h2>Carregamento</h2>
          </div>
          {totalConferidos > 0 && (
            <div className="precarreg-counter">
              <i className="fas fa-check-circle"></i>
              <span>{totalConferidos} palete{totalConferidos !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
        )}

        {renderStepIndicator()}

        {error && (
          <div className="precarreg-alert error">
            <i className="fas fa-exclamation-circle"></i>
            <span>{error}</span>
          </div>
        )}

        {/* ========== Step: Tipo (only non-PWA) ========== */}
        {etapa === 'tipo' && (
          <div className="precarreg-card">
            <div className="precarreg-card-header">
              <i className="fas fa-map-pin"></i>
              <h3>Local do Carregamento</h3>
            </div>
            <p className="precarreg-description">Selecione onde será realizada a conferência de carregamento.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button
                className="precarreg-btn primary"
                onClick={() => handleSelecionarTipo('cliente')}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 12, padding: '16px 20px', textAlign: 'left' }}
              >
                <i className="fas fa-building" style={{ fontSize: '1.2rem' }}></i>
                <div>
                  <div style={{ fontWeight: 600 }}>No Cliente</div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 400, opacity: 0.8 }}>Carregamento no local do cliente</div>
                </div>
              </button>
              <button
                className="precarreg-btn secondary"
                onClick={() => handleSelecionarTipo('base')}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 12, padding: '16px 20px', textAlign: 'left' }}
              >
                <i className="fas fa-warehouse" style={{ fontSize: '1.2rem' }}></i>
                <div>
                  <div style={{ fontWeight: 600 }}>Na Base da Transportadora</div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 400, opacity: 0.7 }}>Carregamento na base com múltiplos destinatários</div>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* ========== Step: CNPJ (No Cliente - non-PWA) ========== */}
        {etapa === 'cnpj' && (
          <div className="precarreg-card">
            <div className="precarreg-card-header">
              <i className="fas fa-building"></i>
              <h3>Identificação do Cliente</h3>
            </div>
            <p className="precarreg-description">Informe o CNPJ do cliente para conferência de carregamento.</p>
            <form onSubmit={handleConsultarCnpj}>
              <div className="precarreg-input-group">
                <label>CNPJ</label>
                <div className="precarreg-input-with-action">
                  <input
                    type="text"
                    inputMode={showKeyboard ? 'text' : 'none'}
                    className="precarreg-input"
                    value={cnpj}
                    onChange={(e) => setCnpj(formatCnpj(e.target.value))}
                    placeholder="00.000.000/0000-00"
                    autoFocus
                    maxLength={18}
                  />
                  <button
                    type="button"
                    className={`precarreg-keyboard-toggle ${showKeyboard ? 'active' : ''}`}
                    onClick={() => setShowKeyboard(!showKeyboard)}
                    title={showKeyboard ? 'Ocultar teclado' : 'Mostrar teclado'}
                  >
                    <i className="fas fa-keyboard"></i>
                  </button>
                </div>
              </div>
              <div className="precarreg-btn-group">
                <button type="button" className="precarreg-btn secondary" onClick={handleVoltar}>
                  <i className="fas fa-arrow-left"></i> Voltar
                </button>
                <button type="submit" className="precarreg-btn primary" disabled={loading || cnpj.replace(/\D/g, '').length !== 14}>
                  {loading ? <><i className="fas fa-spinner fa-spin"></i> Consultando...</> : <><i className="fas fa-search"></i> Consultar</>}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ========== Step: CNPJs Base (Na Base - non-PWA) ========== */}
        {etapa === 'cnpjs_base' && (
          <div className="precarreg-card">
            <div className="precarreg-card-header">
              <i className="fas fa-list"></i>
              <h3>Destinatarios</h3>
            </div>
            <p className="precarreg-description">Adicione os CNPJs dos destinatários do carregamento.</p>

            <form onSubmit={handleAdicionarCnpjBase} style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div className="precarreg-input-with-action" style={{ flex: 1 }}>
                  <input
                    type="text"
                    inputMode={showKeyboard ? 'text' : 'none'}
                    className="precarreg-input"
                    value={cnpjBase}
                    onChange={(e) => setCnpjBase(formatCnpj(e.target.value))}
                    placeholder="00.000.000/0000-00"
                    autoFocus
                    maxLength={18}
                  />
                  <button
                    type="button"
                    className={`precarreg-keyboard-toggle ${showKeyboard ? 'active' : ''}`}
                    onClick={() => setShowKeyboard(!showKeyboard)}
                    title={showKeyboard ? 'Ocultar teclado' : 'Mostrar teclado'}
                  >
                    <i className="fas fa-keyboard"></i>
                  </button>
                </div>
                <button
                  type="submit"
                  className="precarreg-btn primary"
                  disabled={loadingCnpjBase || cnpjBase.replace(/\D/g, '').length !== 14}
                  style={{ width: 'auto', padding: '12px 20px' }}
                >
                  {loadingCnpjBase ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-plus"></i>}
                </button>
              </div>
            </form>

            {/* Lista de clientes adicionados */}
            {clientesBase.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#7f8c8d', marginBottom: 8, textTransform: 'uppercase' }}>
                  Destinatarios ({clientesBase.length})
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {clientesBase.map(cl => (
                    <div key={cl.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 14px', background: '#f0f9ff', borderRadius: 8,
                      borderLeft: '3px solid var(--color-primary, #04364f)'
                    }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#2c3e50' }}>{cl.razao_social}</div>
                        <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>{formatCnpj(cl.cnpj)}</div>
                      </div>
                      <button
                        onClick={() => handleRemoverClienteBase(cl.id)}
                        style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', padding: 4 }}
                        title="Remover"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="precarreg-btn-group">
              <button type="button" className="precarreg-btn secondary" onClick={handleVoltar}>
                <i className="fas fa-arrow-left"></i> Voltar
              </button>
              <button
                type="button"
                className="precarreg-btn primary"
                onClick={handleIniciarBase}
                disabled={clientesBase.length === 0}
              >
                <i className="fas fa-play"></i> Iniciar ({clientesBase.length})
              </button>
            </div>
          </div>
        )}

        {/* ========== Step: Veiculo + Doca ========== */}
        {etapa === 'veiculo_doca' && (
          <div className="precarreg-card">
            <div className="precarreg-card-header">
              <i className="fas fa-truck"></i>
              <h3>Veículo{deveExibirDoca() ? ' e Doca' : ''}</h3>
            </div>
            {cliente && renderClienteInfo(cliente)}
            {tipoLocal === 'base' && clientesBase.length > 0 && (
              <div style={{ padding: '8px 14px', background: '#f0f0f0', borderRadius: 8, marginBottom: 12, fontSize: '0.85rem', color: '#555' }}>
                <i className="fas fa-warehouse" style={{ marginRight: 8 }}></i>
                Carregamento na base - {clientesBase.length} destinatário(s)
              </div>
            )}
            <p className="precarreg-description">
              Informe os dados do veículo{deveExibirDoca() ? ' e da doca' : ''} de carregamento.
            </p>

            <form onSubmit={handleConfirmarVeiculoDoca}>
              <div className="precarreg-input-group">
                <label>Placa do Veículo</label>
                <div className="precarreg-input-with-action">
                  <input
                    ref={veiculoRef}
                    type="text"
                    inputMode={showKeyboard ? 'text' : 'none'}
                    className="precarreg-input mono"
                    value={placaVeiculo}
                    onChange={(e) => setPlacaVeiculo(formatPlaca(e.target.value))}
                    placeholder="ABC1D23"
                    maxLength={7}
                    autoFocus
                  />
                  <button
                    type="button"
                    className={`precarreg-keyboard-toggle ${showKeyboard ? 'active' : ''}`}
                    onClick={() => {
                      setShowKeyboard(!showKeyboard);
                      setTimeout(() => {
                        if (veiculoRef.current) {
                          veiculoRef.current.blur();
                          veiculoRef.current.focus();
                        }
                      }, 100);
                    }}
                    title={showKeyboard ? 'Ocultar teclado' : 'Mostrar teclado'}
                  >
                    <i className="fas fa-keyboard"></i>
                  </button>
                </div>
              </div>
              {deveExibirDoca() && (
                <div className="precarreg-input-group">
                  <label>Doca</label>
                  <input
                    ref={docaRef}
                    type="text"
                    inputMode={showKeyboard ? 'text' : 'none'}
                    className="precarreg-input"
                    value={doca}
                    onChange={(e) => setDoca(e.target.value.toUpperCase())}
                    placeholder="Ex: DOCA 01"
                  />
                </div>
              )}
              <div className="precarreg-btn-group">
                <button type="button" className="precarreg-btn secondary" onClick={handleVoltar}>
                  <i className="fas fa-arrow-left"></i> Voltar
                </button>
                <button type="submit" className="precarreg-btn primary" disabled={placaVeiculo.length < 7}>
                  <i className="fas fa-check"></i> Confirmar
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ========== Step: Palete ========== */}
        {etapa === 'palete' && (
          <>
            {/* Contadores de totais do carregamento */}
            {paletesCarregadosDados.length > 0 && (() => {
              const totalPaletes = paletesCarregadosDados.length;
              const somaPeso = paletesCarregadosDados.reduce((sum, p) => sum + p.peso_total, 0);
              const somaVolumes = paletesCarregadosDados.reduce((sum, p) => sum + p.total_volumes, 0);
              const somaCubagem = paletesCarregadosDados.reduce((sum, p) => sum + p.cubagem_total, 0);
              return (
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                  gap: 10, marginBottom: 16
                }}>
                  <div style={{
                    padding: '12px 14px', background: '#e8f5e9', borderRadius: 10,
                    textAlign: 'center', border: '1px solid #c8e6c9'
                  }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#2e7d32' }}>{totalPaletes}</div>
                    <div style={{ fontSize: '0.75rem', color: '#558b2f', fontWeight: 600, textTransform: 'uppercase' }}>
                      <i className="fas fa-pallet" style={{ marginRight: 4 }}></i>Palete{totalPaletes !== 1 ? 's' : ''}
                    </div>
                  </div>
                  {somaPeso > 0 && (
                    <div style={{
                      padding: '12px 14px', background: '#e3f2fd', borderRadius: 10,
                      textAlign: 'center', border: '1px solid #bbdefb'
                    }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1565c0' }}>
                        {somaPeso.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#1976d2', fontWeight: 600, textTransform: 'uppercase' }}>
                        <i className="fas fa-weight-hanging" style={{ marginRight: 4 }}></i>Peso (kg)
                      </div>
                    </div>
                  )}
                  <div style={{
                    padding: '12px 14px', background: '#f3e5f5', borderRadius: 10,
                    textAlign: 'center', border: '1px solid #e1bee7'
                  }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#7b1fa2' }}>{somaVolumes}</div>
                    <div style={{ fontSize: '0.75rem', color: '#8e24aa', fontWeight: 600, textTransform: 'uppercase' }}>
                      <i className="fas fa-barcode" style={{ marginRight: 4 }}></i>Volume{somaVolumes !== 1 ? 's' : ''}
                    </div>
                  </div>
                  {somaCubagem > 0 && (
                    <div style={{
                      padding: '12px 14px', background: '#fff3e0', borderRadius: 10,
                      textAlign: 'center', border: '1px solid #ffe0b2'
                    }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#e65100' }}>
                        {somaCubagem.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#ef6c00', fontWeight: 600, textTransform: 'uppercase' }}>
                        <i className="fas fa-cube" style={{ marginRight: 4 }}></i>Cubagem (m3)
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="precarreg-card">
              <div className="precarreg-card-header">
                <i className="fas fa-pallet"></i>
                <h3>{paletesCarregadosDados.length > 0 ? 'Próximo Palete' : 'Número do Palete'}</h3>
              </div>
              {tipoLocal === 'cliente' && cliente && renderClienteInfo(cliente)}
              {tipoLocal === 'base' && (
                <div style={{ padding: '8px 14px', background: '#f0f0f0', borderRadius: 8, marginBottom: 12, fontSize: '0.85rem', color: '#555' }}>
                  <i className="fas fa-warehouse" style={{ marginRight: 8 }}></i>
                  Carregamento na base - {clientesBase.length} destinatário(s)
                </div>
              )}
              {(doca || placaVeiculo) && (
                <div style={{ display: 'flex', gap: 12, marginBottom: 12, fontSize: '0.85rem' }}>
                  {doca && (
                    <span style={{ padding: '4px 10px', background: '#e8f5e9', borderRadius: 6, color: '#2e7d32' }}>
                      <i className="fas fa-door-open" style={{ marginRight: 4 }}></i>Doca: {doca}
                    </span>
                  )}
                  {placaVeiculo && (
                    <span style={{ padding: '4px 10px', background: '#e3f2fd', borderRadius: 6, color: '#1565c0' }}>
                      <i className="fas fa-truck" style={{ marginRight: 4 }}></i>Placa: {placaVeiculo}
                    </span>
                  )}
                </div>
              )}
              <p className="precarreg-description">Informe o número do palete que foi conferido no pré-carregamento.</p>
              <form onSubmit={handleConsultarPalete}>
                <div className="precarreg-input-group">
                  <label>Número do Palete</label>
                  <div className="precarreg-input-with-action">
                    <input
                      ref={paleteRef}
                      type="number"
                      inputMode={showKeyboard ? 'numeric' : 'none'}
                      className="precarreg-input"
                      value={numeroPalete}
                      onChange={(e) => setNumeroPalete(e.target.value)}
                      placeholder="Ex: 1"
                      min="1"
                    />
                    <button
                      type="button"
                      className={`precarreg-keyboard-toggle ${showKeyboard ? 'active' : ''}`}
                      onClick={() => {
                        setShowKeyboard(!showKeyboard);
                        setTimeout(() => {
                          if (paleteRef.current) {
                            paleteRef.current.blur();
                            paleteRef.current.focus();
                          }
                        }, 100);
                      }}
                      title={showKeyboard ? 'Ocultar teclado' : 'Mostrar teclado'}
                    >
                      <i className="fas fa-keyboard"></i>
                    </button>
                  </div>
                </div>
                <div className="precarreg-btn-group">
                  <button type="button" className="precarreg-btn secondary" onClick={handleVoltar}>
                    <><i className="fas fa-arrow-left"></i> Voltar</>
                  </button>
                  <button type="submit" className="precarreg-btn primary" disabled={loading || !numeroPalete}>
                    {loading ? <><i className="fas fa-spinner fa-spin"></i> Consultando...</> : <><i className="fas fa-search"></i> Consultar</>}
                  </button>
                </div>
              </form>

              {/* Botão Finalizar Carregamento */}
              {paletesCarregadosDados.length > 0 && (
                <button
                  type="button"
                  className="precarreg-btn primary"
                  onClick={handleFinalizarCarregamento}
                  style={{ width: '100%', marginTop: 12, background: '#27ae60' }}
                >
                  <i className="fas fa-flag-checkered"></i> Finalizar Carregamento ({paletesCarregadosDados.length} palete{paletesCarregadosDados.length !== 1 ? 's' : ''})
                </button>
              )}
            </div>

            {/* Lista de paletes já carregados */}
            {paletesCarregadosDados.length > 0 && (
              <div className="precarreg-card" style={{ marginTop: 16 }}>
                <div className="precarreg-card-header">
                  <i className="fas fa-list-check"></i>
                  <h3>Paletes Carregados ({paletesCarregadosDados.length})</h3>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 350, overflowY: 'auto' }}>
                  {paletesCarregadosDados.map((pal, idx) => (
                    <div key={idx}>
                      <div style={{
                        padding: '8px 12px', borderRadius: 8,
                        background: '#f0fdf4', border: '1px solid #bbf7d0',
                        display: 'flex', alignItems: 'center', gap: 10
                      }}>
                        <div style={{
                          width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                          background: '#27ae60', color: 'white',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.7rem', fontWeight: 700
                        }}>
                          {paletesCarregadosDados.length - idx}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#2c3e50' }}>
                              #{pal.numero_palete}
                            </span>
                            <span style={{ fontSize: '0.78rem', color: '#7f8c8d' }}>
                              {pal.area_codigo || pal.area_nome}{pal.area_codigo && pal.area_nome ? ` (${pal.area_nome})` : ''}
                            </span>
                            {pal.tipo_palete === 'agendamento' && (
                              <span style={{
                                padding: '1px 6px', background: '#fde8d0', color: '#a04000',
                                borderRadius: 4, fontSize: '0.65rem', fontWeight: 600
                              }}>
                                AGEND.
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#95a5a6', flexShrink: 0 }}>
                          {new Date(pal.carregado_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <button
                          onClick={() => setPaleteDetalheIdx(paleteDetalheIdx === idx ? null : idx)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: paleteDetalheIdx === idx ? '#2980b9' : '#95a5a6',
                            padding: '4px 6px', fontSize: '0.9rem', flexShrink: 0
                          }}
                          title="Ver detalhes"
                        >
                          <i className={`fas ${paleteDetalheIdx === idx ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                        </button>
                      </div>
                      {/* Detalhes expandidos */}
                      {paleteDetalheIdx === idx && (
                        <div style={{
                          padding: '10px 14px', marginTop: -1,
                          background: '#f8fffe', border: '1px solid #bbf7d0',
                          borderTop: '1px dashed #c8e6c9',
                          borderRadius: '0 0 8px 8px', fontSize: '0.82rem'
                        }}>
                          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', color: '#546e7a' }}>
                            <span>
                              <i className="fas fa-barcode" style={{ marginRight: 3 }}></i>
                              {pal.total_volumes} vol.
                            </span>
                            {pal.peso_total > 0 && (
                              <span>
                                <i className="fas fa-weight-hanging" style={{ marginRight: 3 }}></i>
                                {pal.peso_total.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} kg
                              </span>
                            )}
                            {pal.cubagem_total > 0 && (
                              <span>
                                <i className="fas fa-cube" style={{ marginRight: 3 }}></i>
                                {pal.cubagem_total.toLocaleString('pt-BR', { maximumFractionDigits: 3 })} m³
                              </span>
                            )}
                            <span style={{ color: '#95a5a6' }}>
                              Op #{pal.numero_operacao}
                            </span>
                          </div>
                          {pal.pedidos.length > 0 && (
                            <div style={{ marginTop: 4, color: '#e65100' }}>
                              <i className="fas fa-file-invoice" style={{ marginRight: 3 }}></i>
                              Pedido{pal.pedidos.length > 1 ? 's' : ''}: {pal.pedidos.join(', ')}
                            </div>
                          )}
                          {tipoLocal === 'base' && pal.cliente_razao_social && (
                            <div style={{ marginTop: 4, color: '#1565c0' }}>
                              <i className="fas fa-building" style={{ marginRight: 3 }}></i>
                              {pal.cliente_razao_social}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* ========== Step: Lista de Paletes (modo lista) ========== */}
        {etapa === 'lista_paletes' && (
          <div className="precarreg-card">
            <div className="precarreg-card-header">
              <i className="fas fa-list-check"></i>
              <h3>Paletes Disponíveis</h3>
            </div>
            {cliente && renderClienteInfo(cliente)}
            {/* Info do cliente selecionado no filtro do conferente */}
            {!cliente && clienteConferenteSelecionado && clientesConferente.length > 0 && (() => {
              const sel = clientesConferente.find(c => c.id === clienteConferenteSelecionado);
              return sel ? (
                <div style={{
                  padding: '8px 14px', background: '#e3f2fd', borderRadius: 8,
                  marginBottom: 12, fontSize: '0.85rem', color: '#1565c0',
                  display: 'flex', alignItems: 'center', gap: 8
                }}>
                  <i className="fas fa-building"></i>
                  <strong>{sel.razao_social}</strong>
                </div>
              ) : null;
            })()}
            {(doca || placaVeiculo) && (
              <div style={{ display: 'flex', gap: 12, marginBottom: 12, fontSize: '0.85rem' }}>
                {doca && (
                  <span style={{ padding: '4px 10px', background: '#e8f5e9', borderRadius: 6, color: '#2e7d32' }}>
                    <i className="fas fa-door-open" style={{ marginRight: 4 }}></i>Doca: {doca}
                  </span>
                )}
                {placaVeiculo && (
                  <span style={{ padding: '4px 10px', background: '#e3f2fd', borderRadius: 6, color: '#1565c0' }}>
                    <i className="fas fa-truck" style={{ marginRight: 4 }}></i>Placa: {placaVeiculo}
                  </span>
                )}
              </div>
            )}

            {/* Filtro de cliente quando conferente tem múltiplos clientes vinculados */}
            {!cliente && clientesConferente.length > 1 && (
              <div style={{
                padding: '12px 14px', background: '#fff8e1', borderRadius: 8,
                border: '1px solid #ffe082', marginBottom: 16
              }}>
                <label style={{
                  display: 'block', fontSize: '0.8rem', fontWeight: 600,
                  color: '#f57f17', marginBottom: 8, textTransform: 'uppercase'
                }}>
                  <i className="fas fa-filter" style={{ marginRight: 6 }}></i>
                  Selecione o cliente
                </label>
                <select
                  className="precarreg-input"
                  value={clienteConferenteSelecionado || ''}
                  onChange={(e) => {
                    const id = e.target.value ? parseInt(e.target.value) : null;
                    setClienteConferenteSelecionado(id);
                    if (id) {
                      setPaletesSelecionados(new Set());
                      carregarListaPaletes(id);
                    } else {
                      setPaletesLista([]);
                    }
                  }}
                  style={{ padding: '10px 12px', fontSize: '0.9rem', width: '100%' }}
                >
                  <option value="">-- Selecione um cliente --</option>
                  {clientesConferente.map(c => (
                    <option key={c.id} value={c.id}>{c.razao_social} ({c.cnpj})</option>
                  ))}
                </select>
              </div>
            )}

            {/* Conferente sem vínculos de cliente */}
            {semVinculosConferente ? (
              <div style={{ textAlign: 'center', padding: '30px 0', color: '#c62828' }}>
                <i className="fas fa-exclamation-triangle" style={{ fontSize: '2rem', marginBottom: 10, display: 'block' }}></i>
                <p style={{ fontWeight: 600 }}>Nenhum cliente vinculado ao seu cadastro</p>
                <p style={{ fontSize: '0.85rem', color: '#7f8c8d', marginTop: 8 }}>
                  Para visualizar paletes, solicite ao administrador que vincule seu usuário aos clientes desejados.
                </p>
              </div>
            ) : loadingLista || loadingClientesConferente ? (
              <div style={{ textAlign: 'center', padding: '30px 0' }}>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: '1.5rem', color: '#3498db' }}></i>
                <p style={{ marginTop: 10, color: '#7f8c8d' }}>
                  {loadingClientesConferente ? 'Verificando clientes vinculados...' : 'Carregando paletes...'}
                </p>
              </div>
            ) : !clienteConferenteSelecionado && !cliente && clientesConferente.length > 1 ? (
              <div style={{ textAlign: 'center', padding: '20px 0', color: '#7f8c8d' }}>
                <i className="fas fa-hand-pointer" style={{ fontSize: '1.5rem', marginBottom: 10, display: 'block' }}></i>
                <p>Selecione um cliente acima para visualizar os paletes</p>
              </div>
            ) : paletesLista.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px 0', color: '#7f8c8d' }}>
                <i className="fas fa-inbox" style={{ fontSize: '2rem', marginBottom: 10, display: 'block' }}></i>
                <p>Nenhum palete encontrado em operações ativas</p>
              </div>
            ) : (
              <>
                {/* Resumo */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', fontSize: '0.85rem' }}>
                  <span style={{ padding: '4px 10px', background: '#f0f0f0', borderRadius: 6 }}>
                    Total: <strong>{paletesLista.length}</strong>
                  </span>
                  <span style={{ padding: '4px 10px', background: '#e8f5e9', borderRadius: 6, color: '#2e7d32' }}>
                    Disponíveis: <strong>{paletesLista.filter(p => p.disponivel).length}</strong>
                  </span>
                  {paletesLista.some(p => p.tem_ponto_corte && !p.tem_divergencia) && (
                    <span style={{ padding: '4px 10px', background: '#fff8e1', borderRadius: 6, color: '#e65100' }}>
                      Com corte: <strong>{paletesLista.filter(p => p.tem_ponto_corte && !p.tem_divergencia).length}</strong>
                    </span>
                  )}
                  {paletesLista.some(p => p.tem_divergencia) && (
                    <span style={{ padding: '4px 10px', background: '#fce4ec', borderRadius: 6, color: '#c62828' }}>
                      Divergentes: <strong>{paletesLista.filter(p => p.tem_divergencia).length}</strong>
                    </span>
                  )}
                  {paletesSelecionados.size > 0 && (
                    <span style={{ padding: '4px 10px', background: '#e3f2fd', borderRadius: 6, color: '#1565c0' }}>
                      Selecionados: <strong>{paletesSelecionados.size}</strong>
                    </span>
                  )}
                </div>

                {/* Totais dos paletes selecionados (etiquetas, peso, cubagem, volumes logísticos) */}
                {paletesSelecionados.size > 0 && (() => {
                  const selecionados = paletesLista.filter(p => paletesSelecionados.has(p.id));
                  const etiquetasSel = selecionados.reduce((sum, p) => sum + (p.total_volumes || 0), 0);
                  const pesoSel = selecionados.reduce((sum, p) => sum + (p.peso_total || 0), 0);
                  const cubSel = selecionados.reduce((sum, p) => sum + (p.cubagem_total || 0), 0);
                  const volSel = selecionados.reduce((sum, p) => sum + (p.volumes_logisticos || 0), 0);
                  if (etiquetasSel === 0 && pesoSel === 0 && cubSel === 0 && volSel === 0) return null;
                  return (
                    <div style={{
                      display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12,
                      padding: '10px 14px', background: '#f0f7ff', borderRadius: 8, border: '1px solid #d0e3ff',
                      fontSize: '0.85rem'
                    }}>
                      <span style={{ fontWeight: 600, color: '#37474f' }}>
                        <i className="fas fa-truck" style={{ marginRight: 6 }}></i>Total selecionado:
                      </span>
                      {etiquetasSel > 0 && (
                        <span style={{ color: '#8e44ad' }}>
                          <i className="fas fa-barcode" style={{ marginRight: 4 }}></i>
                          <strong>{etiquetasSel} volume{etiquetasSel !== 1 ? 's' : ''}</strong>
                        </span>
                      )}
                      {pesoSel > 0 && (
                        <span style={{ color: '#1565c0' }}>
                          <i className="fas fa-weight-hanging" style={{ marginRight: 4 }}></i>
                          <strong>{pesoSel.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg</strong>
                        </span>
                      )}
                      {cubSel > 0 && (
                        <span style={{ color: '#6a1b9a' }}>
                          <i className="fas fa-cube" style={{ marginRight: 4 }}></i>
                          <strong>{cubSel.toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} m³</strong>
                        </span>
                      )}
                      {volSel > 0 && (
                        <span style={{ color: '#2e7d32' }}>
                          <i className="fas fa-boxes-stacked" style={{ marginRight: 4 }}></i>
                          <strong>{volSel} vol.</strong>
                        </span>
                      )}
                    </div>
                  );
                })()}

                {/* Botão selecionar todos */}
                <button
                  type="button"
                  onClick={handleSelecionarTodos}
                  disabled={paletesLista.filter(p => p.disponivel).length === 0}
                  style={{
                    width: '100%', padding: '10px 16px', marginBottom: 12,
                    background: paletesSelecionados.size > 0 && paletesSelecionados.size === paletesLista.filter(p => p.disponivel).length ? '#e3f2fd' : '#f8f9fa',
                    border: '1px solid #ddd', borderRadius: 8, cursor: paletesLista.filter(p => p.disponivel).length === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '0.85rem', fontWeight: 600, color: paletesLista.filter(p => p.disponivel).length === 0 ? '#999' : '#2c3e50',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
                  }}
                >
                  <i className={`fas ${paletesSelecionados.size > 0 && paletesSelecionados.size === paletesLista.filter(p => p.disponivel).length ? 'fa-check-square' : 'fa-square'}`}></i>
                  {paletesSelecionados.size > 0 && paletesSelecionados.size === paletesLista.filter(p => p.disponivel).length ? 'Desselecionar Todos' : 'Selecionar Todos'}
                </button>

                {/* Lista de paletes */}
                <div style={{ maxHeight: 400, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {paletesLista.map(pal => {
                    const selecionado = paletesSelecionados.has(pal.id);
                    const divergente = pal.tem_divergencia;
                    const temCorte = pal.tem_ponto_corte || false;

                    return (
                      <div
                        key={pal.id}
                        onClick={() => handleTogglePalete(pal.id, pal.disponivel)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                          padding: '12px 14px', borderRadius: 8,
                          border: `2px solid ${divergente ? '#ffcdd2' : selecionado ? 'var(--color-primary, #04364f)' : temCorte ? '#ffe0b2' : '#e0e0e0'}`,
                          background: divergente ? '#fce4ec' : selecionado ? '#e8f5e9' : temCorte ? '#fff8e1' : 'white',
                          cursor: divergente ? 'not-allowed' : 'pointer',
                          opacity: divergente ? 0.8 : 1,
                          transition: 'all 0.15s ease'
                        }}
                      >
                        {/* Checkbox */}
                        <div style={{
                          width: 24, height: 24, borderRadius: 4, flexShrink: 0,
                          border: `2px solid ${divergente ? '#e57373' : selecionado ? 'var(--color-primary, #04364f)' : '#bdbdbd'}`,
                          background: selecionado ? 'var(--color-primary, #04364f)' : divergente ? '#ffcdd2' : 'white',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                          {selecionado && <i className="fas fa-check" style={{ color: 'white', fontSize: '0.7rem' }}></i>}
                          {divergente && !selecionado && <i className="fas fa-times" style={{ color: '#c62828', fontSize: '0.7rem' }}></i>}
                        </div>

                        {/* Info */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#2c3e50' }}>
                              Palete #{pal.numero_palete}
                            </span>
                            {divergente && (
                              <span style={{
                                padding: '2px 8px', background: '#c62828', color: 'white',
                                borderRadius: 4, fontSize: '0.7rem', fontWeight: 600
                              }}>
                                DIVERGENTE
                              </span>
                            )}
                            {temCorte && !divergente && (
                              <span style={{
                                padding: '2px 8px', background: '#ff8f00', color: 'white',
                                borderRadius: 4, fontSize: '0.7rem', fontWeight: 600
                              }}>
                                PONTO DE CORTE
                              </span>
                            )}
                            {pal.tipo_palete === 'agendamento' && (
                              <span style={{
                                padding: '2px 8px', background: '#fde8d0', color: '#a04000',
                                borderRadius: 4, fontSize: '0.7rem', fontWeight: 600
                              }}>
                                AGENDAMENTO
                              </span>
                            )}
                          </div>
                          {pal.cliente_razao_social && (
                            <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 2 }}>
                              <i className="fas fa-building" style={{ marginRight: 4, width: 12 }}></i>
                              {pal.cliente_razao_social}
                            </div>
                          )}
                          <div style={{ fontSize: '0.8rem', color: '#7f8c8d', marginTop: 2 }}>
                            <i className="fas fa-map-marker-alt" style={{ marginRight: 4, width: 12 }}></i>
                            {pal.area_codigo || pal.area_nome}
                            {pal.area_codigo && pal.area_nome && <span> ({pal.area_nome})</span>}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: divergente ? '#c62828' : temCorte ? '#e65100' : '#8e44ad', marginTop: 2 }}>
                            {divergente ? (
                              pal.total_volumes === 0
                                ? 'Sem leituras no pré-carregamento'
                                : `${pal.volumes_faltantes} volume(s) faltante(s) de ${pal.total_esperado}`
                            ) : temCorte ? (
                              <><i className="fas fa-cut" style={{ marginRight: 4 }}></i>{pal.total_volumes} volume(s) - corte em {(pal.pedidos_com_corte || []).length} pedido(s)</>
                            ) : (
                              <><i className="fas fa-barcode" style={{ marginRight: 4 }}></i>{pal.total_volumes} volume(s)</>
                            )}
                            {(pal.total_duplicados ?? 0) > 0 && (
                              <span style={{ color: '#f39c12', marginLeft: 8 }}>
                                <i className="fas fa-clone" style={{ marginRight: 3 }}></i>
                                {pal.total_duplicados} duplicada(s)
                              </span>
                            )}
                          </div>
                          {/* Dados logísticos do palete */}
                          {(pal.peso_total || pal.cubagem_total || pal.volumes_logisticos) ? (
                            <div style={{ display: 'flex', gap: 8, fontSize: '0.75rem', color: '#546e7a', marginTop: 3, flexWrap: 'wrap' }}>
                              {pal.peso_total ? (
                                <span><i className="fas fa-weight-hanging" style={{ marginRight: 2 }}></i>{pal.peso_total.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} kg</span>
                              ) : null}
                              {pal.cubagem_total ? (
                                <span><i className="fas fa-cube" style={{ marginRight: 2 }}></i>{pal.cubagem_total.toLocaleString('pt-BR', { maximumFractionDigits: 3 })} m³</span>
                              ) : null}
                              {pal.volumes_logisticos ? (
                                <span><i className="fas fa-boxes-stacked" style={{ marginRight: 2 }}></i>{pal.volumes_logisticos} vol.</span>
                              ) : null}
                            </div>
                          ) : null}
                          {pal.pedidos && pal.pedidos.length > 0 && (
                            <div style={{ fontSize: '0.75rem', color: '#e65100', marginTop: 2 }}>
                              <i className="fas fa-file-invoice" style={{ marginRight: 3 }}></i>
                              Pedido{pal.pedidos.length > 1 ? 's' : ''}: {pal.pedidos.join(', ')}
                            </div>
                          )}
                        </div>

                        {/* Op */}
                        <div style={{ fontSize: '0.75rem', color: '#95a5a6', textAlign: 'right', flexShrink: 0 }}>
                          Op #{pal.numero_operacao}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Veículo (se ainda não preenchido) e Doca (somente no primeiro carregamento se cliente exigir) */}
            {!loadingLista && paletesLista.length > 0 && (
              <div style={{ marginTop: 16, padding: '16px', background: '#f8f9fa', borderRadius: 8, border: '1px solid #e0e0e0' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#7f8c8d', textTransform: 'uppercase' }}>
                    Dados do Veículo
                  </label>
                  <button
                    type="button"
                    className={`precarreg-keyboard-toggle ${showKeyboard ? 'active' : ''}`}
                    onClick={() => setShowKeyboard(!showKeyboard)}
                    title={showKeyboard ? 'Ocultar teclado' : 'Mostrar teclado'}
                    style={{ position: 'static', transform: 'none' }}
                  >
                    <i className="fas fa-keyboard"></i>
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#555', marginBottom: 4 }}>Placa</label>
                    <input
                      type="text"
                      inputMode={showKeyboard ? 'text' : 'none'}
                      className="precarreg-input mono"
                      value={placaVeiculo}
                      onChange={(e) => setPlacaVeiculo(formatPlaca(e.target.value))}
                      placeholder="ABC1D23"
                      maxLength={7}
                      style={{ padding: '8px 12px', fontSize: '0.9rem' }}
                    />
                  </div>
                  {deveExibirDoca() && (
                    <div style={{ flex: 1, minWidth: 140 }}>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: '#555', marginBottom: 4 }}>Doca</label>
                      <input
                        type="text"
                        inputMode={showKeyboard ? 'text' : 'none'}
                        className="precarreg-input"
                        value={doca}
                        onChange={(e) => setDoca(e.target.value.toUpperCase())}
                        placeholder="Ex: DOCA 01"
                        style={{ padding: '8px 12px', fontSize: '0.9rem' }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="precarreg-btn-group" style={{ marginTop: 20 }}>
              <button type="button" className="precarreg-btn secondary" onClick={handleVoltar}>
                <i className="fas fa-arrow-left"></i> Voltar
              </button>
              <button
                type="button"
                className="precarreg-btn primary"
                onClick={handleConfirmarListaPaletes}
                disabled={loading || paletesSelecionados.size === 0 || placaVeiculo.length < 7}
              >
                {loading
                  ? <><i className="fas fa-spinner fa-spin"></i> Registrando...</>
                  : <><i className="fas fa-check"></i> Confirmar ({paletesSelecionados.size})</>
                }
              </button>
            </div>
          </div>
        )}

        {/* ========== Step: Dados + Doca (legacy flow) ========== */}
        {etapa === 'dados_doca' && paleteDados && (
          <div className="precarreg-card">
            <div className="precarreg-card-header">
              <i className="fas fa-clipboard-check"></i>
              <h3>Dados do Pré-Carregamento</h3>
            </div>
            {clientePaleteAtual && renderClienteInfo(clientePaleteAtual)}

            {/* Dados do palete */}
            <div className="precarreg-data-table">
              <table>
                <thead>
                  <tr>
                    <th>Campo</th>
                    <th>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="campo-label"><i className="fas fa-pallet" style={{ marginRight: 4 }}></i>Palete</td>
                    <td className="campo-valor">#{paleteDados.numero_palete}</td>
                  </tr>
                  <tr>
                    <td className="campo-label"><i className="fas fa-map-marker-alt" style={{ marginRight: 4 }}></i>Area de Entrega</td>
                    <td className="campo-valor">{paleteDados.area_codigo || paleteDados.area_nome}{paleteDados.area_codigo && paleteDados.area_nome ? ` (${paleteDados.area_nome})` : ''}</td>
                  </tr>
                  {paleteDados.area_regiao && (
                    <tr>
                      <td className="campo-label"><i className="fas fa-globe-americas" style={{ marginRight: 4 }}></i>Região</td>
                      <td className="campo-valor">{paleteDados.area_regiao}</td>
                    </tr>
                  )}
                  <tr>
                    <td className="campo-label"><i className="fas fa-cogs" style={{ marginRight: 4 }}></i>Operação</td>
                    <td className="campo-valor">#{paleteDados.numero_operacao}</td>
                  </tr>
                  {paleteDados.tipo_palete === 'agendamento' && (
                    <tr>
                      <td className="campo-label">Tipo</td>
                      <td className="campo-valor" style={{ color: '#a04000', fontWeight: 600 }}>
                        <i className="fas fa-calendar-alt" style={{ marginRight: 4 }}></i>
                        Agendamento {paleteDados.data_agendamento ? `- ${new Date(paleteDados.data_agendamento).toLocaleString('pt-BR')}` : ''}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td className="campo-label"><i className="fas fa-barcode" style={{ marginRight: 4 }}></i>Etiquetas Bipadas</td>
                    <td className="campo-valor" style={{ fontWeight: 600, color: '#8e44ad' }}>{totalVolumes} volume(s)</td>
                  </tr>
                  {totaisLogPalete.peso_total > 0 && (
                    <tr>
                      <td className="campo-label"><i className="fas fa-weight-hanging" style={{ marginRight: 4 }}></i>Peso Total</td>
                      <td className="campo-valor" style={{ fontWeight: 600, color: '#1565c0' }}>{totaisLogPalete.peso_total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg</td>
                    </tr>
                  )}
                  {totaisLogPalete.cubagem_total > 0 && (
                    <tr>
                      <td className="campo-label"><i className="fas fa-cube" style={{ marginRight: 4 }}></i>Cubagem Total</td>
                      <td className="campo-valor" style={{ fontWeight: 600, color: '#6a1b9a' }}>{totaisLogPalete.cubagem_total.toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} m³</td>
                    </tr>
                  )}
                  {totaisLogPalete.volumes_total > 0 && (
                    <tr>
                      <td className="campo-label"><i className="fas fa-boxes-stacked" style={{ marginRight: 4 }}></i>Volumes</td>
                      <td className="campo-valor" style={{ fontWeight: 600, color: '#2e7d32' }}>{totaisLogPalete.volumes_total}</td>
                    </tr>
                  )}
                  {pedidosPalete.length > 0 && (
                    <tr>
                      <td className="campo-label"><i className="fas fa-file-invoice" style={{ marginRight: 4 }}></i>Pedido{pedidosPalete.length > 1 ? 's' : ''}</td>
                      <td className="campo-valor" style={{ fontWeight: 600, color: '#e65100' }}>{pedidosPalete.join(', ')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Leituras do pre-carregamento */}
            {leituras.length > 0 && (
              <div style={{ marginTop: 12, maxHeight: 150, overflowY: 'auto' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#7f8c8d', marginBottom: 6, textTransform: 'uppercase' }}>
                  Leituras do Pré-Carregamento
                </label>
                {leituras.map((l, idx) => (
                  <div key={idx} style={{
                    padding: '6px 10px', fontSize: '0.8rem', fontFamily: 'monospace',
                    background: idx % 2 === 0 ? '#f8f9fa' : 'white', borderRadius: 4
                  }}>
                    {l.codigo_lido}
                    <span style={{ float: 'right', color: '#95a5a6', fontSize: '0.75rem' }}>
                      {new Date(l.lido_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <form onSubmit={handleConfirmarDoca} style={{ marginTop: 20 }}>
              <div className="precarreg-input-group">
                <label>Número da Doca</label>
                <div className="precarreg-input-with-action">
                  <input
                    ref={docaRef}
                    type="text"
                    inputMode={showKeyboard ? 'text' : 'none'}
                    className="precarreg-input"
                    value={doca}
                    onChange={(e) => setDoca(e.target.value.toUpperCase())}
                    placeholder="Ex: DOCA 01"
                  />
                  <button
                    type="button"
                    className={`precarreg-keyboard-toggle ${showKeyboard ? 'active' : ''}`}
                    onClick={() => {
                      setShowKeyboard(!showKeyboard);
                      setTimeout(() => {
                        if (docaRef.current) {
                          docaRef.current.blur();
                          docaRef.current.focus();
                        }
                      }, 100);
                    }}
                    title={showKeyboard ? 'Ocultar teclado' : 'Mostrar teclado'}
                  >
                    <i className="fas fa-keyboard"></i>
                  </button>
                </div>
              </div>
              <div className="precarreg-btn-group">
                <button type="button" className="precarreg-btn secondary" onClick={handleVoltar}>
                  <i className="fas fa-arrow-left"></i> Voltar
                </button>
                <button type="submit" className="precarreg-btn primary" disabled={loading || !doca.trim()}>
                  {loading ? <><i className="fas fa-spinner fa-spin"></i> Processando...</> : <><i className="fas fa-check"></i> Confirmar Doca</>}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ========== Step: Veiculo (legacy flow) ========== */}
        {etapa === 'veiculo' && paleteDados && (
          <div className="precarreg-card">
            <div className="precarreg-card-header">
              <i className="fas fa-truck"></i>
              <h3>Veículo de Carregamento</h3>
            </div>

            {/* Resumo */}
            <div style={{
              padding: '12px 16px', background: '#f0f9ff', borderRadius: 8,
              borderLeft: '3px solid var(--color-primary, #04364f)', marginBottom: 16
            }}>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '0.9rem' }}>
                <span><strong>Palete:</strong> #{paleteDados.numero_palete}</span>
                <span><strong>Area:</strong> {paleteDados.area_codigo || paleteDados.area_nome}{paleteDados.area_codigo && paleteDados.area_nome ? ` (${paleteDados.area_nome})` : ''}</span>
                {doca && <span><strong>Doca:</strong> {doca}</span>}
              </div>
              <div style={{ marginTop: 8, fontSize: '0.85rem', color: '#8e44ad', fontWeight: 600 }}>
                <i className="fas fa-barcode" style={{ marginRight: 6 }}></i>
                {totalVolumes} volume(s) de etiquetas bipados no pre-carregamento
              </div>
              {(totaisLogPalete.peso_total > 0 || pedidosPalete.length > 0) && (
                <div style={{ marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: '0.85rem' }}>
                  {totaisLogPalete.peso_total > 0 && (
                    <span style={{ color: '#1565c0', fontWeight: 600 }}>
                      <i className="fas fa-weight-hanging" style={{ marginRight: 4 }}></i>
                      {totaisLogPalete.peso_total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg
                    </span>
                  )}
                  {pedidosPalete.length > 0 && (
                    <span style={{ color: '#e65100', fontWeight: 600 }}>
                      <i className="fas fa-file-invoice" style={{ marginRight: 4 }}></i>
                      Pedido{pedidosPalete.length > 1 ? 's' : ''}: {pedidosPalete.join(', ')}
                    </span>
                  )}
                </div>
              )}
            </div>

            <form onSubmit={handleConfirmarVeiculo}>
              <div className="precarreg-input-group">
                <label>Placa do Veiculo</label>
                <div className="precarreg-input-with-action">
                  <input
                    ref={veiculoRef}
                    type="text"
                    inputMode={showKeyboard ? 'text' : 'none'}
                    className="precarreg-input mono"
                    value={placaVeiculo}
                    onChange={(e) => setPlacaVeiculo(formatPlaca(e.target.value))}
                    placeholder="ABC1D23"
                    maxLength={7}
                  />
                  <button
                    type="button"
                    className={`precarreg-keyboard-toggle ${showKeyboard ? 'active' : ''}`}
                    onClick={() => {
                      setShowKeyboard(!showKeyboard);
                      setTimeout(() => {
                        if (veiculoRef.current) {
                          veiculoRef.current.blur();
                          veiculoRef.current.focus();
                        }
                      }, 100);
                    }}
                    title={showKeyboard ? 'Ocultar teclado' : 'Mostrar teclado'}
                  >
                    <i className="fas fa-keyboard"></i>
                  </button>
                </div>
              </div>
              <div className="precarreg-btn-group">
                <button type="button" className="precarreg-btn secondary" onClick={handleVoltar}>
                  <i className="fas fa-arrow-left"></i> Voltar
                </button>
                <button type="submit" className="precarreg-btn primary" disabled={loading || placaVeiculo.length < 7}>
                  {loading ? <><i className="fas fa-spinner fa-spin"></i> Registrando...</> : <><i className="fas fa-check"></i> Confirmar</>}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ========== Step: Confirmar (pallets subsequentes) ========== */}
        {etapa === 'confirmar' && paleteDados && (
          <div className="precarreg-card">
            <div className="precarreg-card-header">
              <i className="fas fa-clipboard-check"></i>
              <h3>Confirmar Carregamento</h3>
            </div>
            {clientePaleteAtual && renderClienteInfo(clientePaleteAtual)}

            {/* Info badges */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', fontSize: '0.85rem' }}>
              {doca && (
                <span style={{ padding: '4px 10px', background: '#e8f5e9', borderRadius: 6, color: '#2e7d32' }}>
                  <i className="fas fa-door-open" style={{ marginRight: 4 }}></i>Doca: {doca}
                </span>
              )}
              <span style={{ padding: '4px 10px', background: '#e3f2fd', borderRadius: 6, color: '#1565c0' }}>
                <i className="fas fa-truck" style={{ marginRight: 4 }}></i>Placa: {placaVeiculo}
              </span>
            </div>

            <div className="precarreg-data-table">
              <table>
                <thead>
                  <tr>
                    <th>Campo</th>
                    <th>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="campo-label"><i className="fas fa-pallet" style={{ marginRight: 4 }}></i>Palete</td>
                    <td className="campo-valor">#{paleteDados.numero_palete}</td>
                  </tr>
                  <tr>
                    <td className="campo-label"><i className="fas fa-map-marker-alt" style={{ marginRight: 4 }}></i>Area de Entrega</td>
                    <td className="campo-valor">{paleteDados.area_codigo || paleteDados.area_nome}{paleteDados.area_codigo && paleteDados.area_nome ? ` (${paleteDados.area_nome})` : ''}</td>
                  </tr>
                  {paleteDados.area_regiao && (
                    <tr>
                      <td className="campo-label"><i className="fas fa-globe-americas" style={{ marginRight: 4 }}></i>Região</td>
                      <td className="campo-valor">{paleteDados.area_regiao}</td>
                    </tr>
                  )}
                  <tr>
                    <td className="campo-label"><i className="fas fa-cogs" style={{ marginRight: 4 }}></i>Operação</td>
                    <td className="campo-valor">#{paleteDados.numero_operacao}</td>
                  </tr>
                  {paleteDados.tipo_palete === 'agendamento' && (
                    <tr>
                      <td className="campo-label">Tipo</td>
                      <td className="campo-valor" style={{ color: '#a04000', fontWeight: 600 }}>
                        <i className="fas fa-calendar-alt" style={{ marginRight: 4 }}></i>
                        Agendamento {paleteDados.data_agendamento ? `- ${new Date(paleteDados.data_agendamento).toLocaleString('pt-BR')}` : ''}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td className="campo-label">Volumes Bipados</td>
                    <td className="campo-valor" style={{ fontWeight: 600, color: '#8e44ad' }}>{totalVolumes} volume(s)</td>
                  </tr>
                  {totaisLogPalete.peso_total > 0 && (
                    <tr>
                      <td className="campo-label"><i className="fas fa-weight-hanging" style={{ marginRight: 4 }}></i>Peso Total</td>
                      <td className="campo-valor" style={{ fontWeight: 600, color: '#1565c0' }}>{totaisLogPalete.peso_total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg</td>
                    </tr>
                  )}
                  {pedidosPalete.length > 0 && (
                    <tr>
                      <td className="campo-label"><i className="fas fa-file-invoice" style={{ marginRight: 4 }}></i>Pedido{pedidosPalete.length > 1 ? 's' : ''}</td>
                      <td className="campo-valor" style={{ fontWeight: 600, color: '#e65100' }}>{pedidosPalete.join(', ')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="precarreg-btn-group" style={{ marginTop: 20 }}>
              <button className="precarreg-btn secondary" onClick={handleVoltar}>
                <i className="fas fa-arrow-left"></i> Voltar
              </button>
              <button className="precarreg-btn primary" onClick={handleConfirmar} disabled={loading}>
                {loading ? <><i className="fas fa-spinner fa-spin"></i> Registrando...</> : <><i className="fas fa-check"></i> Confirmar</>}
              </button>
            </div>
          </div>
        )}

        {/* ========== Step: Sucesso ========== */}
        {etapa === 'sucesso' && paletesConferidos.length > 0 && (
          <div className="precarreg-card">
            <div className="precarreg-result success">
              <div className="result-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <h3>Carregamento Finalizado</h3>
              <p>
                {paletesConferidos.length} palete{paletesConferidos.length !== 1 ? 's' : ''} registrado{paletesConferidos.length !== 1 ? 's' : ''} com sucesso
              </p>
            </div>

            {/* Info do carregamento */}
            {cliente && (
              <div style={{ marginBottom: 12 }}>
                {renderClienteInfo(cliente)}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', fontSize: '0.85rem' }}>
              {placaVeiculo && (
                <span style={{ padding: '4px 10px', background: '#e3f2fd', borderRadius: 6, color: '#1565c0' }}>
                  <i className="fas fa-truck" style={{ marginRight: 4 }}></i>Placa: {placaVeiculo}
                </span>
              )}
              {doca && (
                <span style={{ padding: '4px 10px', background: '#e8f5e9', borderRadius: 6, color: '#2e7d32' }}>
                  <i className="fas fa-door-open" style={{ marginRight: 4 }}></i>Doca: {doca}
                </span>
              )}
            </div>

            {/* Totais do carregamento */}
            {paletesCarregadosDados.length > 0 && (() => {
              const somaPeso = paletesCarregadosDados.reduce((sum, p) => sum + p.peso_total, 0);
              const somaVolumes = paletesCarregadosDados.reduce((sum, p) => sum + p.total_volumes, 0);
              const somaCubagem = paletesCarregadosDados.reduce((sum, p) => sum + p.cubagem_total, 0);
              const somaVolLogisticos = paletesCarregadosDados.reduce((sum, p) => sum + p.volumes_logisticos, 0);
              return (
                <div style={{
                  display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16,
                  padding: '12px 16px', background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0',
                  fontSize: '0.9rem', justifyContent: 'center'
                }}>
                  <span style={{ fontWeight: 600, color: '#2e7d32' }}>
                    <i className="fas fa-pallet" style={{ marginRight: 6 }}></i>
                    {paletesCarregadosDados.length} palete{paletesCarregadosDados.length !== 1 ? 's' : ''}
                  </span>
                  {somaPeso > 0 && (
                    <span style={{ fontWeight: 600, color: '#1565c0' }}>
                      <i className="fas fa-weight-hanging" style={{ marginRight: 4 }}></i>
                      {somaPeso.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg
                    </span>
                  )}
                  {somaVolumes > 0 && (
                    <span style={{ fontWeight: 600, color: '#7b1fa2' }}>
                      <i className="fas fa-barcode" style={{ marginRight: 4 }}></i>
                      {somaVolumes} volume{somaVolumes !== 1 ? 's' : ''}
                    </span>
                  )}
                  {somaCubagem > 0 && (
                    <span style={{ fontWeight: 600, color: '#6a1b9a' }}>
                      <i className="fas fa-cube" style={{ marginRight: 4 }}></i>
                      {somaCubagem.toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} m³
                    </span>
                  )}
                  {somaVolLogisticos > 0 && (
                    <span style={{ fontWeight: 600, color: '#37474f' }}>
                      <i className="fas fa-boxes-stacked" style={{ marginRight: 4 }}></i>
                      {somaVolLogisticos} vol. logístico{somaVolLogisticos !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              );
            })()}

            {/* Lista de paletes carregados (modo individual) */}
            {paletesCarregadosDados.length > 0 && (
              <div style={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {paletesCarregadosDados.map((pal, idx) => (
                  <div key={idx} style={{
                    padding: '10px 14px', background: '#f0fdf4', borderRadius: 8,
                    border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: 10
                  }}>
                    <i className="fas fa-check-circle" style={{ color: '#27ae60', fontSize: '1.1rem', flexShrink: 0 }}></i>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#2c3e50' }}>
                        Palete #{pal.numero_palete}
                        <span style={{ fontWeight: 400, color: '#7f8c8d', marginLeft: 8, fontSize: '0.8rem' }}>
                          {pal.area_codigo || pal.area_nome}{pal.area_codigo && pal.area_nome ? ` (${pal.area_nome})` : ''}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: '0.78rem', color: '#546e7a', marginTop: 2 }}>
                        <span><i className="fas fa-barcode" style={{ marginRight: 2 }}></i>{pal.total_volumes} vol.</span>
                        {pal.peso_total > 0 && <span><i className="fas fa-weight-hanging" style={{ marginRight: 2 }}></i>{pal.peso_total.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} kg</span>}
                        {pal.cubagem_total > 0 && <span><i className="fas fa-cube" style={{ marginRight: 2 }}></i>{pal.cubagem_total.toLocaleString('pt-BR', { maximumFractionDigits: 3 })} m³</span>}
                        {pal.pedidos.length > 0 && (
                          <span style={{ color: '#e65100' }}><i className="fas fa-file-invoice" style={{ marginRight: 2 }}></i>Pedido{pal.pedidos.length > 1 ? 's' : ''}: {pal.pedidos.join(', ')}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Lista simples para modo lista (sem paletesCarregadosDados) */}
            {paletesCarregadosDados.length === 0 && paletesConferidos.length > 0 && (
              <div style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {paletesConferidos.map((num, idx) => (
                  <div key={idx} style={{
                    padding: '8px 14px', background: '#e8f5e9', borderRadius: 6,
                    display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem'
                  }}>
                    <i className="fas fa-check-circle" style={{ color: '#27ae60' }}></i>
                    <span style={{ fontWeight: 600 }}>Palete #{num}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="precarreg-btn-group" style={{ marginTop: 20 }}>
              <button className="precarreg-btn secondary" onClick={handleReset}>
                <i className="fas fa-redo"></i> {isPWA ? 'Voltar' : 'Nova Sessão'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

export default CarregamentoConferencia;
